// Copyright (c) Docugami, Inc. All rights reserved.

import { ClusterDocument } from '../../networking/api'
import { FetchState } from '../shared_types'

export interface DocumentState {
    clusterDocument: ClusterDocument;
}

export enum CanvasSelectionMode {
    Select = 0,
    Highlight,
    TextEdit,
    RectangularSelect
}

export enum ModalSelectionItemType {
    None,
    Chunk,
    Entry
}

export enum ModalSelectionArea {
    Canvas = 'Canvas',
    ReportWorkPane = 'ReportWorkPane', // Includes the selected item and report items
    SelectedItem = 'SelectedItem',
    ReportItems = 'ReportItems'
}

export interface ModalSelectionDisplayContext {
    title: string;
    defaultMessage: string;
    selectionReady?: boolean;
    selectedMessage?: string;
    formatArgs?: string[];
    modalColor?: string;
}

export interface ModalSelectionContext {
    isActive: boolean;
    itemType: ModalSelectionItemType;
    autoConfirm?: boolean;
    areas?: ModalSelectionArea[];
    displayContext?: ModalSelectionDisplayContext;
    dropTarget?: boolean;
}

export interface DocumentsState {
    indexed: { [id: string]: DocumentState };
    allIds: string[];

    // BEGIN: UX Related Information
    selectionMode: CanvasSelectionMode;
    selectionModeLocked?: boolean;
    documentViewerHeight: number;
    modalSelectionContext: ModalSelectionContext;
    // END: UX Related Information
}

export interface DocumentFetchStatePayload {
    docId: string;
    fetchState: FetchState;
}

export interface DocumentBooleanPayload {
    docId: string;
    value: boolean;
}

export interface PagePayload {
    docId: string;
    page: number;
}

export function InitialDocState(documentInfo: ClusterDocument): DocumentState {
    return {
        clusterDocument: documentInfo,
    };
}

export const APPEND_DOCUMENT = "APPEND_DOCUMENT";
export const APPEND_DOCUMENTS = "APPEND_DOCUMENTS";
export const UPDATE_DOCUMENT = "UPDATE_DOCUMENT";
export const SET_DEFAULT_INSTANCES_FETCHSTATE = "SET_DEFAULT_INSTANCES_FETCHSTATE";
export const SET_SELECTION_MODE = "SET_SELECTION_MODE";
export const SET_SELECTION_MODE_LOCKED = "SET_SELECTION_MODE_LOCKED";
export const SET_DOCUMENT_VIEWER_HEIGHT = "SET_DOCUMENT_VIEWER_HEIGHT";
export const SET_MODAL_SELECTION_CONTEXT = "SET_MODAL_SELECTION_CONTEXT";
export const SET_MODAL_DISPLAY_CONTEXT = "SET_MODAL_DISPLAY_CONTEXT";
export const SET_DOCUMENT_PAGE_COUNT = "SET_DOCUMENT_PAGE_COUNT";

export interface DocumentsPayload {
    indexed: { [id: string]: DocumentState };
    allIds: string[];
}

export interface DocumentPayload {
    document: ClusterDocument
}

export interface DocumentBooleanPayload {
    docId: string;
    value: boolean;
}

export interface DocumentStringPayload {
    docId: string;
    value: string;
}

export interface DocumentNumberPayload {
    docId: string;
    value: number;
}

interface AppendDocuments {
    type: typeof APPEND_DOCUMENTS;
    payload: DocumentsPayload;
}

interface AppendDocument {
    type: typeof APPEND_DOCUMENT;
    payload: DocumentState;
}

interface SetSelectionMode {
    type: typeof SET_SELECTION_MODE;
    payload: CanvasSelectionMode;
}

interface SetSelectionModeLocked {
    type: typeof SET_SELECTION_MODE_LOCKED;
    payload: boolean;
}

interface SetDocumentViewerHeight {
    type: typeof SET_DOCUMENT_VIEWER_HEIGHT;
    payload: number;
}

interface SetModalSelectionContext {
    type: typeof SET_MODAL_SELECTION_CONTEXT;
    payload: ModalSelectionContext;
}

interface SetModalDisplayContext {
    type: typeof SET_MODAL_DISPLAY_CONTEXT;
    payload: Partial<ModalSelectionDisplayContext>;
}

interface SetDocumentPageCount {
    type: typeof SET_DOCUMENT_PAGE_COUNT;
    payload: DocumentNumberPayload;
}

interface UpdateDocument {
    type: typeof UPDATE_DOCUMENT;
    payload: DocumentPayload;
}

export type DocumentsActionTypes =
    AppendDocuments |
    SetSelectionMode |
    SetSelectionModeLocked |
    SetDocumentViewerHeight |
    SetModalSelectionContext |
    SetModalDisplayContext |
    AppendDocument |
    SetDocumentPageCount |
    UpdateDocument
